import React, { useState } from 'react';
import { FormControl, Select, MenuItem, Typography } from '@mui/material';
import { ArrowDropDown } from '@mui/icons-material';
import { useAppTheme } from '../../theme/ThemeProvider/AppTheme';

export const RoundedSelect = ({
  value,
  onChange,
  options = [],
  placeholder = 'Select an option',
  width = '100%',
  displayEmpty = true,
  renderValue,
  sx = {},
  containerSx = {},
  menuHeader = null,
  menuProps = {},
  required = false,
  ...props
}) => {
  const { getColor } = useAppTheme();

  const [isViewed, setIsViewed] = useState(false);

  const error = required && isViewed && !value;

  function handleOnChange(event) {
    const { value } = event.target;
    onChange?.(event, value);
  }

  const defaultRenderValue = (selected) => {
    return (
      <Typography
        sx={{
          color: error ? 'var(--color-error)' : 'var(--color-text-secondary)',
          fontSize: '14px',
        }}
      >
        {typeof selected === 'string'
          ? (options?.find((e) => e?.value === selected)?.label ?? placeholder)
          : selected?.join(', ')}
        <span style={{ visibility: 'hidden', opacity: '0' }}>_</span>
      </Typography>
    );
  };

  return (
    <FormControl sx={{ width: width, ...containerSx }}>
      <Select
        {...props}
        value={value ?? ''}
        onChange={handleOnChange}
        onBlur={(_) => required && setIsViewed(true)}
        displayEmpty={displayEmpty}
        renderValue={renderValue ?? defaultRenderValue}
        MenuProps={{
          disableAutoFocusItem: true,
          PaperProps: {
            style: {
              borderRadius: '12px',
              border: `1px solid ${error ? 'var(--color-error)' : getColor('text', 0.2)}`,
              boxShadow: `0 10px 15px -3px ${getColor('text', 0.1)}, 0 4px 6px -2px ${getColor('text', 0.05)}`,
              marginTop: '4px',
            },
          },
          ...menuProps,
        }}
        sx={{
          borderRadius: '12px',
          bgcolor: error ? 'var(--color-error-light)' : 'var(--color-bg)',
          border: error ? '1px solid var(--color-error)' : `1px solid ${getColor('text', 0.2)}`,
          transition: 'all 0.2s ease',
          '& .MuiOutlinedInput-notchedOutline': {
            border: 'none',
          },
          '&:hover': {
            bgcolor: error ? 'var(--color-error-light)' : 'var(--color-bg)',
            borderColor: error ? 'var(--color-error)' : `1px solid ${getColor('primary', 0.2)}`,
          },
          '&.Mui-focused': {
            bgcolor: 'var(--color-bg)',
            borderColor: error ? 'var(--color-error)' : 'var(--color-primary)',
            boxShadow: `0 0 0 3px ${getColor(error ? 'error' : 'primary', 0.1)}`,
            borderRadius: '12px',
          },
          '& .MuiSelect-select': {
            fontSize: '14px',
            fontWeight: 400,
            color: error ? 'var(--color-error)' : 'var(--color-text-secondary)',
            bgcolor: 'var(--color-bg)',
            borderRadius: '12px',
            py: 1.25,
            px: 1.5,
          },
          ...sx,
        }}
        IconComponent={(props) => (
          <ArrowDropDown {...props} style={{ color: 'var(--color-text)', mr: 1 }} />
        )}
      >
        {menuHeader}
        {options.map((option) => (
          <MenuItem value={option.value} key={option.id || option.value}>
            {option.label || option.value}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};
